import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/base/Modal';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Button from '../../components/base/Button';
import { openBase64File } from '../../utils/fileViewer';

// Interfaces
interface PlannedInvestment {
  id: string;
  name: string;
  description: string;
  plannedAmount: number;
  plannedDate: string;
  category: string;
  priority: 'hoch' | 'mittel' | 'niedrig';
  status: 'geplant' | 'genehmigt' | 'abgelehnt';
  notes: string;
}

interface InvestmentPosition {
  id: string;
  name: string;
  description: string;
  amount: number;
  dueDate: string;
  isPaid: boolean;
  paidDate?: string;
  invoiceUrl?: string;
  notes: string;
}

interface RunningInvestment {
  id: string;
  name: string;
  description: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  startDate: string;
  expectedEndDate: string;
  lender: string;
  interestRate: number;
  monthlyPayment: number;
  status: 'aktiv' | 'pausiert' | 'abgeschlossen';
  notes: string;
  positions?: InvestmentPosition[];
}

interface CompletedInvestment {
  id: string;
  name: string;
  description: string;
  totalAmount: number;
  startDate: string;
  endDate: string;
  lender: string;
  roi: number;
  notes: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  supplier: string;
  description: string;
  amount: number;
  dueDate: string;
  isPaid: boolean;
  paidDate?: string;
  category: string;
  notes: string;
}

interface Sale {
  id: string;
  date: string;
  type: 'automat_verkauf' | 'produkt_verkauf' | 'sonstiges';
  description: string;
  amount: number;
  buyer: string;
  paymentMethod: string;
  notes: string;
}

const categories = [
  'Automaten',
  'Fahrzeuge',
  'Immobilien',
  'Ausstattung',
  'Marketing',
  'IT & Software',
  'Sonstiges'
];

const invoiceCategories = [
  'Miete',
  'Strom',
  'Wasser',
  'Internet',
  'Versicherung',
  'Wartung',
  'Lieferanten',
  'Sonstiges'
];

// Helper function to check if payment is due soon
const isDueSoon = (dueDate: string, daysThreshold: number): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffTime = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= daysThreshold;
};

// Helper function to format currency
const formatCurrency = (amount: number) => {
  if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount)) {
    return 'CHF 0.00';
  }
  return `CHF ${amount.toFixed(2)}`;
};

export default function FinanzplanungPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'planned' | 'running'>('overview');

  // States
  const [plannedInvestments, setPlannedInvestments] = useState<PlannedInvestment[]>([]);
  const [runningInvestments, setRunningInvestments] = useState<RunningInvestment[]>([]);
  const [completedInvestments, setCompletedInvestments] = useState<CompletedInvestment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);

  // Priority filter state - moved to component level
  const [priorityFilter, setPriorityFilter] = useState<'alle' | 'hoch' | 'mittel' | 'niedrig'>('alle');

  // Modal states
  const [isPlannedModalOpen, setIsPlannedModalOpen] = useState(false);
  const [isRunningModalOpen, setIsRunningModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState<RunningInvestment | null>(null);

  // Form states
  const [plannedForm, setPlannedForm] = useState({
    name: '',
    description: '',
    plannedAmount: '',
    plannedDate: '',
    category: '',
    priority: 'mittel' as const,
    notes: ''
  });

  const [runningForm, setRunningForm] = useState({
    name: '',
    description: '',
    totalAmount: '',
    months: '',
    startDate: '',
    expectedEndDate: '',
    lender: '',
    interestRate: '',
    monthlyPayment: '',
    notes: '',
    positions: [] as Array<{
      id: string;
      name: string;
      description: string;
      amount: number;
      dueDate: string;
      notes: string;
    }>
  });

  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNumber: '',
    supplier: '',
    description: '',
    amount: '',
    dueDate: '',
    category: '',
    notes: ''
  });

  const [saleForm, setSaleForm] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'automat_verkauf' as const,
    description: '',
    amount: '',
    buyer: '',
    paymentMethod: '',
    notes: ''
  });

  const [positionForm, setPositionForm] = useState({
    name: '',
    description: '',
    amount: '',
    dueDate: '',
    notes: ''
  });

  // Running Investment Detail Modal State
  const [selectedRunningInvestment, setSelectedRunningInvestment] = useState<any>(null);
  const [investmentPositions, setInvestmentPositions] = useState<any[]>([]);
  const [newPosition, setNewPosition] = useState({
    description: '',
    amount: '',
    dueDate: '',
    notes: ''
  });

  // Repayment Plan State
  const [repaymentSchedule, setRepaymentSchedule] = useState<any[]>([]);
  const [editingPosition, setEditingPosition] = useState<InvestmentPosition | null>(null);
  const [editingRepayment, setEditingRepayment] = useState<any>(null);
  const [repaymentEditForm, setRepaymentEditForm] = useState({
    amount: ''
  });
  
  // Edit Running Investment State
  const [isEditRunningModalOpen, setIsEditRunningModalOpen] = useState(false);
  const [editingRunningInvestment, setEditingRunningInvestment] = useState<RunningInvestment | null>(null);

  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);

      // Load planned investments
      const { data: plannedData, error: plannedError } = await supabase
        .from('finanzplanung')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'planned_investment')
        .order('created_at', { ascending: false });

      if (plannedError) throw plannedError;
      setPlannedInvestments((plannedData || []).map(item => ({ id: item.id, ...item.data })));

      // Load running investments
      const { data: runningData, error: runningError } = await supabase
        .from('finanzplanung')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'running_investment')
        .order('created_at', { ascending: false });

      if (runningError) throw runningError;
      setRunningInvestments((runningData || []).map(item => ({ id: item.id, ...item.data })));

      // Load completed investments
      const { data: completedData, error: completedError } = await supabase
        .from('finanzplanung')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'completed_investment')
        .order('created_at', { ascending: false });

      if (completedError) throw completedError;
      setCompletedInvestments((completedData || []).map(item => ({ id: item.id, ...item.data })));

      // Load invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('finanzplanung')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'invoice')
        .order('created_at', { ascending: false });

      if (invoicesError) throw invoicesError;
      setInvoices((invoicesData || []).map(item => ({ id: item.id, ...item.data })));

      // Load sales
      const { data: salesData, error: salesError } = await supabase
        .from('finanzplanung')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'sale')
        .order('created_at', { ascending: false });

      if (salesError) throw salesError;
      setSales((salesData || []).map(item => ({ id: item.id, ...item.data })));

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate warnings
  const getPaymentWarnings = () => {
    const warnings: Array<{ type: 'position' | 'repayment'; message: string; dueDate: string }> = [];

    // Check positions in running investments (3 days threshold)
    runningInvestments.forEach(investment => {
      investment.positions?.forEach(position => {
        if (!position.isPaid && isDueSoon(position.dueDate, 3)) {
          const daysLeft = Math.ceil((new Date(position.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
          warnings.push({
            type: 'position',
            message: `${investment.name} - ${position.name}: ${formatCurrency(position.amount)} (${daysLeft === 0 ? 'Heute fällig' : `Fällig in ${daysLeft} Tag${daysLeft !== 1 ? 'en' : ''}` })`,
            dueDate: position.dueDate
          });
        }
      });

      // Check repayment schedule (2 days threshold)
      if (investment.repaymentSchedule) {
        investment.repaymentSchedule.forEach((item: any) => {
          if (!item.isPaid && isDueSoon(item.dueDate, 2)) {
            const daysLeft = Math.ceil((new Date(item.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
            warnings.push({
              type: 'repayment',
              message: `${investment.name} - Rückzahlung ${item.monthName}: ${formatCurrency(item.amount)} (${daysLeft === 0 ? 'Heute fällig' : `Fällig in ${daysLeft} Tag${daysLeft !== 1 ? 'en' : ''}` })`,
              dueDate: item.dueDate
            });
          }
        });
      }
    });

    return warnings;
  };

  const paymentWarnings = getPaymentWarnings();

  // Planned Investment handlers
  const handlePlannedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const investmentData = {
        name: plannedForm.name,
        description: plannedForm.description,
        plannedAmount: parseFloat(plannedForm.plannedAmount),
        plannedDate: plannedForm.plannedDate,
        category: plannedForm.category,
        priority: plannedForm.priority,
        status: 'geplant' as const,
        notes: plannedForm.notes
      };

      const { error } = await supabase
        .from('finanzplanung')
        .insert({
          user_id: user?.id,
          type: 'planned_investment',
          data_type: 'planned_investment',
          data: investmentData
        });

      if (error) throw error;

      await loadAllData();
      setIsPlannedModalOpen(false);
      setPlannedForm({
        name: '',
        description: '',
        plannedAmount: '',
        plannedDate: '',
        category: '',
        priority: 'mittel',
        notes: ''
      });
      alert('✅ Geplante Investition wurde erfolgreich hinzugefügt!');
    } catch (error) {
      console.error('Error saving planned investment:', error);
      alert('❌ Fehler beim Speichern der geplanten Investition.');
    }
  };

  // Running Investment handlers
  const handleRunningSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const totalAmount = parseFloat(runningForm.totalAmount);
      const months = parseInt(runningForm.months) || 0;
      const monthlyPayment = months > 0 ? totalAmount / months : 0;
      
      const investmentData = {
        name: runningForm.name,
        description: runningForm.description,
        totalAmount, // This is the original total amount from the form
        paidAmount: 0,
        remainingAmount: totalAmount,
        startDate: runningForm.startDate,
        expectedEndDate: runningForm.expectedEndDate,
        lender: runningForm.lender,
        interestRate: parseFloat(runningForm.interestRate) || 0,
        monthlyPayment,
        months,
        status: 'aktiv' as const,
        notes: runningForm.notes,
        positions: runningForm.positions.map(pos => ({
          ...pos,
          isPaid: false
        }))
      };

      const { error } = await supabase
        .from('finanzplanung')
        .insert({
          user_id: user?.id,
          type: 'running_investment',
          data_type: 'running_investment',
          data: investmentData
        });

      if (error) throw error;

      await loadAllData();
      setIsRunningModalOpen(false);
      setRunningForm({
        name: '',
        description: '',
        totalAmount: '',
        months: '',
        startDate: '',
        expectedEndDate: '',
        lender: '',
        interestRate: '',
        monthlyPayment: '',
        notes: '',
        positions: []
      });
      alert('✅ Laufende Investition wurde erfolgreich hinzugefügt!');
    } catch (error) {
      console.error('Error saving running investment:', error);
      alert('❌ Fehler beim Speichern der laufenden Investition.');
    }
  };

  // Handle running form changes with automatic calculations
  const handleRunningFormChange = (field: string, value: string) => {
    setRunningForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Calculate monthly payment when totalAmount, months, or interestRate change
      if (field === 'totalAmount' || field === 'months' || field === 'interestRate') {
        const total = parseFloat(updated.totalAmount) || 0;
        const months = parseInt(updated.months) || 0;
        const yearlyRate = parseFloat(updated.interestRate) || 0;
        
        if (months > 0 && total > 0) {
          if (yearlyRate > 0) {
            // Calculate with interest using annuity formula
            const monthlyRate = yearlyRate / 100 / 12; // Convert yearly % to monthly decimal
            const monthlyPayment = total * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
            updated.monthlyPayment = monthlyPayment.toFixed(2);
          } else {
            // Calculate without interest (simple division)
            updated.monthlyPayment = (total / months).toFixed(2);
          }
        } else {
          updated.monthlyPayment = '';
        }
      }
      
      // Calculate end date when startDate or months change
      if ((field === 'startDate' || field === 'months') && updated.startDate && updated.months) {
        const startDate = new Date(updated.startDate);
        const months = parseInt(updated.months);
        if (!isNaN(months) && months > 0) {
          const endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + months);
          updated.expectedEndDate = endDate.toISOString().split('T')[0];
        }
      }
      
      return updated;
    });
  };

  // Add position to running form
  const handleAddPositionToForm = () => {
    const newPosition = {
      id: crypto.randomUUID(),
      name: positionForm.name,
      description: positionForm.description,
      amount: parseFloat(positionForm.amount),
      dueDate: positionForm.dueDate,
      notes: positionForm.notes
    };

    setRunningForm(prev => ({
      ...prev,
      positions: [...prev.positions, newPosition]
    }));

    // Reset position form
    setPositionForm({
      name: '',
      description: '',
      amount: '',
      dueDate: '',
      notes: ''
    });
  };

  // Remove position from running form
  const handleRemovePositionFromForm = (positionId: string) => {
    setRunningForm(prev => ({
      ...prev,
      positions: prev.positions.filter(pos => pos.id !== positionId)
    }));
  };

  // Invoice handlers
  const handleInvoiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const invoiceData = {
        invoiceNumber: invoiceForm.invoiceNumber,
        supplier: invoiceForm.supplier,
        description: invoiceForm.description,
        amount: parseFloat(invoiceForm.amount),
        dueDate: invoiceForm.dueDate,
        isPaid: false,
        category: invoiceForm.category,
        notes: invoiceForm.notes
      };

      const { error } = await supabase
        .from('finanzplanung')
        .insert({
          user_id: user?.id,
          type: 'invoice',
          data_type: 'invoice',
          data: invoiceData
        });

      if (error) throw error;

      await loadAllData();
      setIsInvoiceModalOpen(false);
      setInvoiceForm({
        invoiceNumber: '',
        supplier: '',
        description: '',
        amount: '',
        dueDate: '',
        category: '',
        notes: ''
      });
      alert('✅ Rechnung wurde erfolgreich hinzugefügt!');
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('❌ Fehler beim Speichern der Rechnung.');
    }
  };

  const toggleInvoicePaid = async (invoice: Invoice) => {
    try {
      const updatedData = {
        ...invoice,
        isPaid: !invoice.isPaid,
        paidDate: !invoice.isPaid ? new Date().toISOString().split('T')[0] : undefined
      };

      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedData })
        .eq('id', invoice.id);

      if (error) throw error;

      await loadAllData();
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('❌ Fehler beim Aktualisieren der Rechnung.');
    }
  };

  // Delete running investment
  const handleDeleteRunningInvestment = async (id: string, name: string) => {
    if (!confirm(`Möchten Sie die laufende Investition "${name}" wirklich löschen?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('finanzplanung')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadAllData();
      alert('✅ Laufende Investition wurde erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting running investment:', error);
      alert('❌ Fehler beim Löschen der laufenden Investition.');
    }
  };

  // Edit running investment
  const handleEditRunningInvestment = (investment: RunningInvestment) => {
    setEditingRunningInvestment(investment);
    
    // Calculate months from start and end date
    const start = new Date(investment.startDate);
    const end = new Date(investment.expectedEndDate);
    const months = Math.round(
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth())
    );
    
    setRunningForm({
      name: investment.name,
      description: investment.description,
      totalAmount: investment.totalAmount.toString(),
      months: months.toString(),
      startDate: investment.startDate,
      expectedEndDate: investment.expectedEndDate,
      lender: investment.lender,
      interestRate: investment.interestRate.toString(),
      monthlyPayment: investment.monthlyPayment.toString(),
      notes: investment.notes,
      positions: investment.positions || []
    });
    
    setIsEditRunningModalOpen(true);
  };

  const handleUpdateRunningInvestment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingRunningInvestment) return;

    try {
      const totalAmount = parseFloat(runningForm.totalAmount);
      const months = parseInt(runningForm.months) || 0;
      const monthlyPayment = months > 0 ? totalAmount / months : 0;
      
      const updatedData = {
        ...editingRunningInvestment,
        name: runningForm.name,
        description: runningForm.description,
        totalAmount,
        startDate: runningForm.startDate,
        expectedEndDate: runningForm.expectedEndDate,
        lender: runningForm.lender,
        interestRate: parseFloat(runningForm.interestRate) || 0,
        monthlyPayment,
        months,
        notes: runningForm.notes,
        positions: runningForm.positions
      };

      // Recalculate repayment schedule if dates changed
      if (updatedData.startDate !== editingRunningInvestment.startDate || 
          updatedData.expectedEndDate !== editingRunningInvestment.expectedEndDate ||
          updatedData.totalAmount !== editingRunningInvestment.totalAmount) {
        
        const newSchedule = generateRepaymentSchedule(
          updatedData.totalAmount,
          updatedData.startDate,
          updatedData.expectedEndDate,
          editingRunningInvestment.repaymentSchedule || []
        );
        
        updatedData.repaymentSchedule = newSchedule;
        
        // Recalculate paid amounts
        const totalRepaymentAmount = newSchedule.reduce((sum: number, s: any) => sum + s.amount, 0);
        const paidRepaymentAmount = newSchedule.filter((s: any) => s.isPaid).reduce((sum: number, s: any) => sum + s.amount, 0);
        updatedData.paidAmount = paidRepaymentAmount;
        updatedData.remainingAmount = Math.max(0, totalAmount - paidRepaymentAmount);
      }

      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedData })
        .eq('id', editingRunningInvestment.id);

      if (error) throw error;

      await loadAllData();
      setIsEditRunningModalOpen(false);
      setEditingRunningInvestment(null);
      setRunningForm({
        name: '',
        description: '',
        totalAmount: '',
        months: '',
        startDate: '',
        expectedEndDate: '',
        lender: '',
        interestRate: '',
        monthlyPayment: '',
        notes: '',
        positions: []
      });
      alert('✅ Laufende Investition wurde erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating running investment:', error);
      alert('❌ Fehler beim Aktualisieren der laufenden Investition.');
    }
  };

  // Complete running investment
  const handleCompleteInvestment = async (investment: RunningInvestment) => {
    if (!confirm(`Möchten Sie die Investition "${investment.name}" als abgeschlossen markieren?`)) {
      return;
    }

    try {
      // Update status to 'abgeschlossen'
      const updatedInvestment = {
        ...investment,
        status: 'abgeschlossen' as const
      };

      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedInvestment })
        .eq('id', investment.id);

      if (error) throw error;

      await loadAllData();
      alert('✅ Investition wurde erfolgreich als abgeschlossen markiert!');
    } catch (error) {
      console.error('Error completing investment:', error);
      alert('❌ Fehler beim Abschließen der Investition.');
    }
  };

  // Sale handlers
  const handleSaleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const saleData = {
        date: saleForm.date,
        type: saleForm.type,
        description: saleForm.description,
        amount: parseFloat(saleForm.amount),
        buyer: saleForm.buyer,
        paymentMethod: saleForm.paymentMethod,
        notes: saleForm.notes
      };

      const { error } = await supabase
        .from('finanzplanung')
        .insert({
          user_id: user?.id,
          type: 'sale',
          data_type: 'sale',
          data: saleData
        });

      if (error) throw error;

      await loadAllData();
      setIsSaleModalOpen(false);
      setSaleForm({
        date: new Date().toISOString().split('T')[0],
        type: 'automat_verkauf',
        description: '',
        amount: '',
        buyer: '',
        paymentMethod: '',
        notes: ''
      });
      alert('✅ Verkauf wurde erfolgreich erfasst!');
    } catch (error) {
      console.error('Error saving sale:', error);
      alert('❌ Fehler beim Erfassen des Verkaufs.');
    }
  };

  // Open investment detail
  const handleOpenDetail = (investment: RunningInvestment) => {
    setSelectedInvestment(investment);
    
    // Generate repayment schedule
    if (investment.startDate && investment.expectedEndDate && investment.totalAmount) {
      const schedule = generateRepaymentSchedule(
        investment.totalAmount,
        investment.startDate,
        investment.expectedEndDate,
        investment.repaymentSchedule || []
      );
      setRepaymentSchedule(schedule);
    } else {
      setRepaymentSchedule([]);
    }
    
    setIsDetailModalOpen(true);
  };

  // Generate repayment schedule
  const generateRepaymentSchedule = (
    totalAmount: number,
    startDate: string,
    endDate: string,
    existingSchedule: any[] = []
  ) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Calculate number of months
    const months = Math.round(
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth())
    );
    
    if (months <= 0) return [];
    
    // Use totalAmount from investment for monthly calculation
    const monthlyAmount = totalAmount / months;
    const schedule = [];
    
    const monthNames = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    for (let i = 0; i < months; i++) {
      const dueDate = new Date(start);
      dueDate.setMonth(start.getMonth() + i);
      
      // Check if this month exists in existing schedule
      const existing = existingSchedule.find((s: any) => s.month === i);
      
      schedule.push({
        month: i,
        monthName: monthNames[dueDate.getMonth()] + ' ' + dueDate.getFullYear(),
        dueDate: dueDate.toISOString().split('T')[0],
        amount: monthlyAmount,
        isPaid: existing?.isPaid || false,
        paidDate: existing?.paidDate || null,
        confirmationFile: existing?.confirmationFile || null
      });
    }
    
    return schedule;
  };

  // Toggle repayment paid status
  const toggleRepaymentPaid = async (item: any) => {
    if (!selectedInvestment) return;

    try {
      // Update the schedule with the payment status
      const updatedSchedule = repaymentSchedule.map(s => {
        if (s.month === item.month) {
          return {
            ...s,
            isPaid: !s.isPaid,
            paidDate: !s.isPaid ? new Date().toISOString().split('T')[0] : null
          };
        }
        return s;
      });

      // Calculate total paid with actual amounts
      const totalPaid = updatedSchedule
        .filter(s => s.isPaid)
        .reduce((sum, s) => sum + s.amount, 0);

      // Calculate position amounts
      const totalPositionAmount = selectedInvestment.positions?.reduce((sum, pos) => sum + pos.amount, 0) || 0;
      const paidPositionAmount = selectedInvestment.positions?.filter(pos => pos.isPaid).reduce((sum, pos) => sum + pos.amount, 0) || 0;

      // WICHTIG: totalAmount bleibt IMMER der ursprüngliche Investitionsbetrag
      const updatedInvestment = {
        ...selectedInvestment,
        repaymentSchedule: updatedSchedule,
        // totalAmount wird NICHT verändert - es bleibt der ursprüngliche Wert
        paidAmount: totalPaid,
        remainingAmount: Math.max(0, selectedInvestment.totalAmount - totalPaid)
      };

      // Save to database
      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedInvestment })
        .eq('id', selectedInvestment.id);

      if (error) throw error;

      setRepaymentSchedule(updatedSchedule);
      setSelectedInvestment(updatedInvestment);
      await loadAllData();
    } catch (error) {
      console.error('Error updating repayment:', error);
      alert('❌ Fehler beim Aktualisieren der Zahlung.');
    }
  };

  const handleEditRepayment = (item: any) => {
    setEditingRepayment(item);
    setRepaymentEditForm({
      amount: item.amount.toString()
    });
  };

  const handleUpdateRepayment = async () => {
    if (!selectedInvestment || !editingRepayment) return;

    try {
      const newAmount = parseFloat(repaymentEditForm.amount);
      
      if (isNaN(newAmount) || newAmount <= 0) {
        alert('❌ Bitte geben Sie einen gültigen Betrag ein.');
        return;
      }
      
      const totalInvestmentAmount = selectedInvestment.totalAmount;
      
      // Calculate how much has been paid before this month
      let paidBeforeThisMonth = 0;
      for (let i = 0; i < repaymentSchedule.length; i++) {
        const item = repaymentSchedule[i];
        if (item.month < editingRepayment.month && item.isPaid) {
          paidBeforeThisMonth += item.amount;
        }
      }
      
      // Calculate remaining debt after this month's payment
      const remainingDebtAfterThisMonth = totalInvestmentAmount - paidBeforeThisMonth - newAmount;
      
      // If remaining debt is negative, the payment is too high
      if (remainingDebtAfterThisMonth < 0) {
        alert('❌ Der eingegebene Betrag ist zu hoch! Es verbleiben nur noch ' + formatCurrency(totalInvestmentAmount - paidBeforeThisMonth) + ' zu zahlen.');
        return;
      }
      
      // Create updated schedule
      const updatedSchedule: any[] = [];
      
      // Count how many months are after the edited month (unpaid)
      const monthsAfterEdit = repaymentSchedule.filter(s => s.month > editingRepayment.month).length;
      
      // Calculate new monthly amount for remaining months
      const newMonthlyAmount = monthsAfterEdit > 0 ? remainingDebtAfterThisMonth / monthsAfterEdit : 0;
      
      let totalPaid = 0;
      
      for (let i = 0; i < repaymentSchedule.length; i++) {
        const item = repaymentSchedule[i];
        
        if (item.month < editingRepayment.month) {
          // Months before the edited month - keep as is
          updatedSchedule.push(item);
          if (item.isPaid) {
            totalPaid += item.amount;
          }
        } else if (item.month === editingRepayment.month) {
          // The edited month - use new amount
          const monthData = {
            ...item,
            amount: newAmount
          };
          updatedSchedule.push(monthData);
          if (item.isPaid) {
            totalPaid += newAmount;
          }
        } else {
          // Months after the edited month - recalculate with new monthly amount
          if (remainingDebtAfterThisMonth > 0) {
            const monthData = {
              ...item,
              amount: newMonthlyAmount
            };
            updatedSchedule.push(monthData);
            if (item.isPaid) {
              totalPaid += newMonthlyAmount;
            }
          }
          // If remainingDebtAfterThisMonth <= 0, don't add this month (investment is paid off)
        }
      }

      const updatedInvestment = {
        ...selectedInvestment,
        repaymentSchedule: updatedSchedule,
        paidAmount: totalPaid,
        remainingAmount: Math.max(0, totalInvestmentAmount - totalPaid)
      };

      // Save to database immediately
      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedInvestment })
        .eq('id', selectedInvestment.id);

      if (error) throw error;

      // Update local state
      setRepaymentSchedule(updatedSchedule);
      setSelectedInvestment(updatedInvestment);
      setEditingRepayment(null);
      setRepaymentEditForm({ amount: '' });
      
      // Reload all data to ensure consistency
      await loadAllData();
      
      if (updatedSchedule.length < repaymentSchedule.length) {
        alert(`✅ Betrag wurde erfolgreich gespeichert!\n\n${repaymentSchedule.length - updatedSchedule.length} Monat(e) wurden entfernt, da die Investition früher abbezahlt wird.\n\nNeue monatliche Rate für verbleibende Monate: ${formatCurrency(newMonthlyAmount)}`);
      } else if (monthsAfterEdit > 0) {
        alert(`✅ Betrag wurde erfolgreich gespeichert!\n\nDie verbleibenden ${monthsAfterEdit} Monate wurden automatisch angepasst.\nNeue monatliche Rate: ${formatCurrency(newMonthlyAmount)}`);
      } else {
        alert('✅ Betrag wurde erfolgreich gespeichert!');
      }
    } catch (error) {
      console.error('Error updating repayment:', error);
      alert('❌ Fehler beim Speichern des Betrags.');
    }
  };

  // Handle repayment confirmation upload
  const handleRepaymentConfirmationUpload = async (item: any, file: File) => {
    if (!selectedInvestment) return;

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        
        const updatedSchedule = repaymentSchedule.map(s => {
          if (s.month === item.month) {
            return { ...s, confirmationFile: base64 };
          }
          return s;
        });

        const updatedInvestment = {
          ...selectedInvestment,
          repaymentSchedule: updatedSchedule
        };

        const { error } = await supabase
          .from('finanzplanung')
          .update({ data: updatedInvestment })
          .eq('id', selectedInvestment.id);

        if (error) throw error;

        setRepaymentSchedule(updatedSchedule);
        setSelectedInvestment(updatedInvestment);
        await loadAllData();
        alert('✅ Zahlungsbestätigung wurde erfolgreich hochgeladen!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading confirmation:', error);
      alert('❌ Fehler beim Hochladen der Zahlungsbestätigung.');
    }
  };

  // Position handlers
  const handlePositionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedInvestment) return;

    try {
      const newPosition: InvestmentPosition = {
        id: crypto.randomUUID(),
        name: positionForm.name,
        description: positionForm.description,
        amount: parseFloat(positionForm.amount),
        dueDate: positionForm.dueDate || '', // Allow empty dueDate
        isPaid: false,
        notes: positionForm.notes
      };

      const updatedPositions = [...(selectedInvestment.positions || []), newPosition];
      
      // Calculate position totals
      const totalPositionAmount = updatedPositions.reduce((sum, pos) => sum + pos.amount, 0);
      const paidPositionAmount = updatedPositions
        .filter(pos => pos.isPaid)
        .reduce((sum, pos) => sum + pos.amount, 0);

      // WICHTIG: totalAmount bleibt IMMER der ursprüngliche Investitionsbetrag
      const updatedInvestment = {
        ...selectedInvestment,
        positions: updatedPositions,
        // totalAmount wird NICHT verändert - es bleibt der ursprüngliche Wert
        paidAmount: paidPositionAmount,
        remainingAmount: totalPositionAmount - paidPositionAmount
      };

      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedInvestment })
        .eq('id', selectedInvestment.id);

      if (error) throw error;

      setSelectedInvestment(updatedInvestment);
      await loadAllData();
      setIsPositionModalOpen(false);
      setPositionForm({
        name: '',
        description: '',
        amount: '',
        dueDate: '',
        notes: ''
      });
      alert('✅ Position wurde erfolgreich hinzugefügt!');
    } catch (error) {
      console.error('Error saving position:', error);
      alert('❌ Fehler beim Speichern der Position.');
    }
  };

  const togglePositionPaid = async (positionId: string) => {
    if (!selectedInvestment) return;

    try {
      const updatedPositions = selectedInvestment.positions?.map(pos => {
        if (pos.id === positionId) {
          return {
            ...pos,
            isPaid: !pos.isPaid,
            paidDate: !pos.isPaid ? new Date().toISOString().split('T')[0] : undefined
          };
        }
        return pos;
      }) || [];

      const paidPositionAmount = updatedPositions
        .filter(pos => pos.isPaid)
        .reduce((sum, pos) => sum + pos.amount, 0);

      const totalPositionAmount = updatedPositions.reduce((sum, pos) => sum + pos.amount, 0);

      // WICHTIG: totalAmount bleibt IMMER der ursprüngliche Investitionsbetrag
      const updatedInvestment = {
        ...selectedInvestment,
        positions: updatedPositions,
        // totalAmount wird NICHT verändert - es bleibt der ursprüngliche Wert
        paidAmount: paidPositionAmount,
        remainingAmount: totalPositionAmount - paidPositionAmount
      };

      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedInvestment })
        .eq('id', selectedInvestment.id);

      if (error) throw error;

      setSelectedInvestment(updatedInvestment);
      await loadAllData();
    } catch (error) {
      console.error('Error updating position:', error);
      alert('❌ Fehler beim Aktualisieren der Position.');
    }
  };

  const handleEditPosition = (position: InvestmentPosition) => {
    setPositionForm({
      name: position.name,
      description: position.description,
      amount: position.amount.toString(),
      dueDate: position.dueDate,
      notes: position.notes
    });
    setEditingPosition(position);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedInvestment || !editingPosition) return;

    try {
      const updatedPositions = selectedInvestment.positions?.map(pos => {
        if (pos.id === editingPosition.id) {
          return {
            ...pos,
            name: positionForm.name,
            description: positionForm.description,
            amount: parseFloat(positionForm.amount),
            dueDate: positionForm.dueDate || '', // Allow empty dueDate
            notes: positionForm.notes
          };
        }
        return pos;
      }) || [];

      const totalPositionAmount = updatedPositions.reduce((sum, pos) => sum + pos.amount, 0);
      const paidPositionAmount = updatedPositions
        .filter(pos => pos.isPaid)
        .reduce((sum, pos) => sum + pos.amount, 0);

      // WICHTIG: totalAmount bleibt IMMER der ursprüngliche Investitionsbetrag
      const updatedInvestment = {
        ...selectedInvestment,
        positions: updatedPositions,
        // totalAmount wird NICHT verändert - es bleibt der ursprüngliche Wert
        paidAmount: paidPositionAmount,
        remainingAmount: totalPositionAmount - paidPositionAmount
      };

      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedInvestment })
        .eq('id', selectedInvestment.id);

      if (error) throw error;

      setSelectedInvestment(updatedInvestment);
      await loadAllData();
      setIsPositionModalOpen(false);
      setEditingPosition(null);
      setPositionForm({
        name: '',
        description: '',
        amount: '',
        dueDate: '',
        notes: ''
      });
      alert('✅ Position wurde erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating position:', error);
      alert('❌ Fehler beim Aktualisieren der Position.');
    }
  };

  const handleInvoiceUpload = async (positionId: string, file: File) => {
    if (!selectedInvestment) return;

    try {
      // Convert file to base64 for storage in data field
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        
        // Update position with invoice data
        const updatedPositions = selectedInvestment.positions?.map(pos => {
          if (pos.id === positionId) {
            return { ...pos, invoiceUrl: base64 };
          }
          return pos;
        }) || [];

        const updatedInvestment = {
          ...selectedInvestment,
          positions: updatedPositions
        };

        const { error } = await supabase
          .from('finanzplanung')
          .update({ data: updatedInvestment })
          .eq('id', selectedInvestment.id);

        if (error) throw error;

        setSelectedInvestment(updatedInvestment);
        await loadAllData();
        alert('✅ Rechnung wurde erfolgreich hochgeladen!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading invoice:', error);
      alert('❌ Fehler beim Hochladen der Rechnung.');
    }
  };

  const deletePosition = async (positionId: string) => {
    if (!selectedInvestment) return;
    
    if (!confirm('Möchten Sie diese Position wirklich löschen?')) {
      return;
    }

    try {
      const updatedPositions = selectedInvestment.positions?.filter(pos => pos.id !== positionId) || [];
      
      const paidPositionAmount = updatedPositions
        .filter(pos => pos.isPaid)
        .reduce((sum, pos) => sum + pos.amount, 0);

      const totalPositionAmount = updatedPositions.reduce((sum, pos) => sum + pos.amount, 0);

      // WICHTIG: totalAmount bleibt IMMER der ursprüngliche Investitionsbetrag
      const updatedInvestment = {
        ...selectedInvestment,
        positions: updatedPositions,
        // totalAmount wird NICHT verändert - es bleibt der ursprüngliche Wert
        paidAmount: paidPositionAmount,
        remainingAmount: totalPositionAmount - paidPositionAmount
      };

      const { error } = await supabase
        .from('finanzplanung')
        .update({ data: updatedInvestment })
        .eq('id', selectedInvestment.id);

      if (error) throw error;

      setSelectedInvestment(updatedInvestment);
      await loadAllData();
      alert('✅ Position wurde erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting position:', error);
      alert('❌ Fehler beim Löschen der Position.');
    }
  };

  // Calculate statistics
  const totalPlannedAmount = plannedInvestments
    .filter(inv => inv.status === 'geplant')
    .reduce((sum, inv) => sum + inv.plannedAmount, 0);

  // Calculate running investments totals - use totalAmount from investment
  const totalRunningAmount = runningInvestments
    .filter(inv => inv.status === 'aktiv')
    .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

  const totalUnpaidInvoices = invoices
    .filter(inv => !inv.isPaid)
    .reduce((sum, inv) => sum + inv.amount, 0);

  const overdueInvoices = invoices.filter(inv => 
    !inv.isPaid && new Date(inv.dueDate) < new Date()
  );

  const totalSalesAmount = sales.reduce((sum, sale) => sum + sale.amount, 0);

  // Render Overview Tab
  const renderOverview = () => {
    return (
      <div className="space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-blue-900">Geplante Investitionen</h3>
              <i className="ri-calendar-line text-2xl text-blue-600"></i>
            </div>
            <p className="text-3xl font-bold text-blue-900">{formatCurrency(totalPlannedAmount)}</p>
            <p className="text-xs text-blue-700 mt-1">{plannedInvestments.length} Investition{plannedInvestments.length !== 1 ? 'en' : ''}</p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border-2 border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-orange-900">Laufende Investitionen</h3>
              <i className="ri-line-chart-line text-2xl text-orange-600"></i>
            </div>
            <p className="text-3xl font-bold text-orange-900">{formatCurrency(totalRunningAmount)}</p>
            <p className="text-xs text-orange-700 mt-1">{runningInvestments.filter(inv => inv.status === 'aktiv').length} aktiv</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border-2 border-red-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-red-900">Offene Rechnungen</h3>
              <i className="ri-file-list-line text-2xl text-red-600"></i>
            </div>
            <p className="text-3xl font-bold text-red-900">{formatCurrency(totalUnpaidInvoices)}</p>
            <p className="text-xs text-red-700 mt-1">{invoices.filter(inv => !inv.isPaid).length} unbezahlt</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-2 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-900">Verkäufe</h3>
              <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
            </div>
            <p className="text-3xl font-bold text-green-900">{formatCurrency(totalSalesAmount)}</p>
            <p className="text-xs text-green-700 mt-1">{sales.length} Verkauf{sales.length !== 1 ? 'e' : ''}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            <i className="ri-flashlight-line mr-2"></i>
            Schnellaktionen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => setIsPlannedModalOpen(true)}
              className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap text-sm font-medium"
            >
              <i className="ri-add-line mr-2"></i>
              Geplante Investition
            </button>
            <button
              onClick={() => setIsRunningModalOpen(true)}
              className="px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer whitespace-nowrap text-sm font-medium"
            >
              <i className="ri-add-line mr-2"></i>
              Laufende Investition
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Planned Investments */}
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              <i className="ri-calendar-line mr-2"></i>
              Neueste geplante Investitionen
            </h3>
            {plannedInvestments.length === 0 ? (
              <p className="text-gray-500 text-sm">Keine geplanten Investitionen vorhanden</p>
            ) : (
              <div className="space-y-3">
                {plannedInvestments.slice(0, 3).map(investment => (
                  <div key={investment.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{investment.name}</h4>
                        <p className="text-sm text-gray-600">{investment.category}</p>
                      </div>
                      <span className="font-bold text-blue-600">{formatCurrency(investment.plannedAmount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Running Investments */}
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              <i className="ri-line-chart-line mr-2"></i>
              Aktive Investitionen
            </h3>
            {runningInvestments.filter(inv => inv.status === 'aktiv').length === 0 ? (
              <p className="text-gray-500 text-sm">Keine aktiven Investitionen vorhanden</p>
            ) : (
              <div className="space-y-3">
                {runningInvestments.filter(inv => inv.status === 'aktiv').slice(0, 3).map(investment => (
                  <div key={investment.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{investment.name}</h4>
                        <p className="text-sm text-gray-600">{investment.lender}</p>
                      </div>
                      <span className="font-bold text-orange-600">{formatCurrency(investment.totalAmount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Planned Investments Tab
  const renderPlannedInvestments = () => {
    const filteredInvestments = plannedInvestments.filter(inv => {
      if (priorityFilter === 'alle') return true;
      return inv.priority === priorityFilter;
    });

    const highPriorityCount = plannedInvestments.filter(inv => inv.priority === 'hoch').length;
    const totalPlanned = plannedInvestments.reduce((sum, inv) => sum + inv.plannedAmount, 0);

    return (
      <div className="space-y-6">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-blue-900">Gesamtvolumen</h3>
              <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
            </div>
            <p className="text-3xl font-bold text-blue-900">{formatCurrency(totalPlanned)}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border-2 border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-purple-900">Anzahl Investitionen</h3>
              <i className="ri-list-check text-2xl text-purple-600"></i>
            </div>
            <p className="text-3xl font-bold text-purple-900">{plannedInvestments.length}</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border-2 border-red-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-red-900">Hohe Priorität</h3>
              <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
            </div>
            <p className="text-3xl font-bold text-red-900">{highPriorityCount}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Filter:</span>
            <button
              onClick={() => setPriorityFilter('alle')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer ${
                priorityFilter === 'alle'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle ({plannedInvestments.length})
            </button>
            <button
              onClick={() => setPriorityFilter('hoch')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer ${
                priorityFilter === 'hoch'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Hoch ({plannedInvestments.filter(inv => inv.priority === 'hoch').length})
            </button>
            <button
              onClick={() => setPriorityFilter('mittel')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer ${
                priorityFilter === 'mittel'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Mittel ({plannedInvestments.filter(inv => inv.priority === 'mittel').length})
            </button>
            <button
              onClick={() => setPriorityFilter('niedrig')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer ${
                priorityFilter === 'niedrig'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Niedrig ({plannedInvestments.filter(inv => inv.priority === 'niedrig').length})
            </button>
          </div>
        </div>

        {/* Add Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setIsPlannedModalOpen(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap font-medium"
          >
            <i className="ri-add-line mr-2"></i>
            Neue geplante Investition
          </button>
        </div>

        {/* Investments List */}
        {filteredInvestments.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <i className="ri-calendar-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Keine geplanten Investitionen</h3>
            <p className="text-gray-500 mb-6">Erstellen Sie Ihre erste geplante Investition</p>
            <button
              onClick={() => setIsPlannedModalOpen(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Jetzt erstellen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInvestments.map(investment => {
              const priorityColors = {
                hoch: 'from-red-50 to-red-100 border-red-300',
                mittel: 'from-yellow-50 to-yellow-100 border-yellow-300',
                niedrig: 'from-green-50 to-green-100 border-green-300'
              };

              const priorityBadgeColors = {
                hoch: 'bg-red-600 text-white',
                mittel: 'bg-yellow-600 text-white',
                niedrig: 'bg-green-600 text-white'
              };

              const daysUntilDue = Math.ceil((new Date(investment.plannedDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
              const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 30;

              return (
                <div
                  key={investment.id}
                  className={`bg-gradient-to-br ${priorityColors[investment.priority]} rounded-lg p-6 border-2 shadow-lg hover:shadow-xl transition-shadow`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{investment.name}</h3>
                      <p className="text-sm text-gray-600">{investment.description}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${priorityBadgeColors[investment.priority]}`}>
                      {investment.priority.toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Betrag:</span>
                      <span className="text-xl font-bold text-gray-900">{formatCurrency(investment.plannedAmount)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Kategorie:</span>
                      <span className="text-sm font-medium text-gray-900">{investment.category}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Geplant für:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(investment.plannedDate).toLocaleDateString('de-DE')}
                      </span>
                    </div>

                    {isDueSoon && (
                      <div className="bg-orange-100 border border-orange-300 rounded-lg p-2 mt-2">
                        <p className="text-xs text-orange-800 font-medium">
                          <i className="ri-alarm-warning-line mr-1"></i>
                          {daysUntilDue === 0 ? 'Heute fällig!' : daysUntilDue === 1 ? 'Morgen fällig!' : `Fällig in ${daysUntilDue} Tagen`}
                        </p>
                      </div>
                    )}

                    {investment.notes && (
                      <div className="pt-2 border-t border-gray-300">
                        <p className="text-xs text-gray-600">{investment.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        if (!confirm(`Möchten Sie "${investment.name}" als laufende Investition starten?`)) return;
                        
                        try {
                          const runningData = {
                            name: investment.name,
                            description: investment.description,
                            totalAmount: investment.plannedAmount,
                            paidAmount: 0,
                            remainingAmount: investment.plannedAmount,
                            startDate: new Date().toISOString().split('T')[0],
                            expectedEndDate: investment.plannedDate,
                            lender: '',
                            interestRate: 0,
                            monthlyPayment: 0,
                            status: 'aktiv' as const,
                            notes: investment.notes,
                            positions: []
                          };

                          const { error } = await supabase
                            .from('finanzplanung')
                            .insert({
                              user_id: user?.id,
                              type: 'running_investment',
                              data_type: 'running_investment',
                              data: runningData
                            });

                          if (error) throw error;

                          await supabase
                            .from('finanzplanung')
                            .delete()
                            .eq('id', investment.id);

                          await loadAllData();
                          alert('✅ Investition wurde erfolgreich gestartet!');
                        } catch (error) {
                          console.error('Error starting investment:', error);
                          alert('❌ Fehler beim Starten der Investition.');
                        }
                      }}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap text-sm font-medium"
                    >
                      <i className="ri-play-line mr-1"></i>
                      Starten
                    </button>
                    <button
                      onClick={() => {
                        setPlannedForm({
                          name: investment.name,
                          description: investment.description,
                          plannedAmount: investment.plannedAmount.toString(),
                          plannedDate: investment.plannedDate,
                          category: investment.category,
                          priority: investment.priority,
                          notes: investment.notes
                        });
                        setIsPlannedModalOpen(true);
                      }}
                      className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap text-sm font-medium border border-gray-300"
                    >
                      <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm(`Möchten Sie "${investment.name}" wirklich löschen?`)) return;
                        
                        try {
                          const { error } = await supabase
                            .from('finanzplanung')
                            .delete()
                            .eq('id', investment.id);

                          if (error) throw error;

                          await loadAllData();
                          alert('✅ Investition wurde erfolgreich gelöscht!');
                        } catch (error) {
                          console.error('Error deleting investment:', error);
                          alert('❌ Fehler beim Löschen der Investition.');
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer whitespace-nowrap text-sm font-medium"
                    >
                      <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render Running Investments Tab
  const renderRunningInvestments = () => {
    return (
      <div className="space-y-6">
        {/* Add Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setIsRunningModalOpen(true)}
            className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer whitespace-nowrap font-medium"
          >
            <i className="ri-add-line mr-2"></i>
            Neue laufende Investition
          </button>
        </div>

        {/* Investments List */}
        {runningInvestments.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <i className="ri-line-chart-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Keine laufenden Investitionen</h3>
            <p className="text-gray-500 mb-6">Erstellen Sie Ihre erste laufende Investition</p>
            <button
              onClick={() => setIsRunningModalOpen(true)}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Jetzt erstellen
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {runningInvestments.map(investment => {
              const totalPositionAmount = investment.positions?.reduce((sum, pos) => sum + pos.amount, 0) || 0;
              const paidPositionAmount = investment.positions?.filter(pos => pos.isPaid).reduce((sum, pos) => sum + pos.amount, 0) || 0;
              const unpaidPositionAmount = totalPositionAmount - paidPositionAmount;
              
              // Calculate repayment progress (monthly payments)
              const repaymentSchedule = investment.repaymentSchedule || [];
              const totalRepaymentAmount = repaymentSchedule.reduce((sum: number, s: any) => sum + s.amount, 0);
              const paidRepaymentAmount = repaymentSchedule.filter((s: any) => s.isPaid).reduce((sum: number, s: any) => sum + s.amount, 0);
              const unpaidRepaymentAmount = totalRepaymentAmount - paidRepaymentAmount;
              const repaymentProgressPercentage = totalRepaymentAmount > 0 ? (paidRepaymentAmount / totalRepaymentAmount) * 100 : 0;

              return (
                <div
                  key={investment.id}
                  className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{investment.name}</h3>
                        {investment.status === 'abgeschlossen' && (
                          <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                            Abgeschlossen
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{investment.description}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-purple-50 rounded-lg p-4">
                      <span className="text-xs text-purple-800 font-medium">Gesamtbetrag (Investition)</span>
                      <p className="text-2xl font-bold text-purple-900">{formatCurrency(investment.totalAmount)}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <span className="text-xs text-blue-800 font-medium">Laufende Kostenpositionen Gesamt</span>
                      <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalPositionAmount)}</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <span className="text-xs text-orange-800 font-medium">Verbleibend (Positionen)</span>
                      <p className="text-2xl font-bold text-orange-900">{formatCurrency(unpaidPositionAmount)}</p>
                    </div>
                  </div>

                  {/* Progress Bar - Monatliche Rückzahlungen */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Fortschritt (Monatliche Rückzahlungen)</span>
                      <span className="text-sm font-semibold text-gray-900">{repaymentProgressPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-teal-600 to-blue-600 h-3 rounded-full transition-all"
                        style={{ width: `${repaymentProgressPercentage}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                      <span>Zurückbezahlt: {formatCurrency(paidRepaymentAmount)}</span>
                      <span>Noch offen: {formatCurrency(unpaidRepaymentAmount)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                    <div>
                      <span className="text-gray-600">Kreditgeber:</span>
                      <p className="font-medium text-gray-900">{investment.lender || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Startdatum:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(investment.startDate).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Enddatum:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(investment.expectedEndDate).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Zinssatz:</span>
                      <p className="font-medium text-gray-900">{investment.interestRate}%</p>
                    </div>
                  </div>

                  {investment.notes && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-sm text-gray-700">{investment.notes}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenDetail(investment)}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap font-medium"
                    >
                      <i className="ri-eye-line mr-2"></i>
                      Details anzeigen
                    </button>
                    {investment.status !== 'abgeschlossen' && (
                      <>
                        <button
                          onClick={() => handleEditRunningInvestment(investment)}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer whitespace-nowrap font-medium"
                        >
                          <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                        </button>
                        <button
                          onClick={() => handleCompleteInvestment(investment)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer whitespace-nowrap font-medium"
                        >
                          <i className="ri-check-line mr-2"></i>
                          Abschließen
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDeleteRunningInvestment(investment.id, investment.name)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer whitespace-nowrap font-medium"
                    >
                      <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Finanzplanungsdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          {/* Header */}
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                  <i className="ri-line-chart-line mr-3 text-purple-600"></i>
                  Finanzplanung
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Verwalten Sie Investitionen, Rechnungen und Verkäufe
                </p>
              </div>
            </div>
          </div>

          {/* Payment Warnings */}
          {paymentWarnings.length > 0 && (
            <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 bg-red-50 border-b-2 border-red-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <i className="ri-alarm-warning-fill text-2xl text-red-600 mr-3"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-red-900 mb-2">
                    ⚠️ Fällige Zahlungen ({paymentWarnings.length})
                  </h3>
                  <div className="space-y-1.5">
                    {paymentWarnings.map((warning, index) => (
                      <div key={index} className="flex items-start">
                        <i className={`${warning.type === 'position' ? 'ri-file-list-3-line' : 'ri-calendar-check-line'} text-red-600 mr-2 mt-0.5`}></i>
                        <p className="text-sm text-red-800 font-medium">
                          {warning.message}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-red-700 mt-2">
                    <i className="ri-information-line mr-1"></i>
                    Bitte bezahlen Sie diese Positionen rechtzeitig. Die Warnung verschwindet automatisch nach der Zahlung.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex gap-2 overflow-x-auto pb-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'overview'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Übersicht
              </button>
              <button
                onClick={() => setActiveTab('planned')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'planned'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Geplante Investitionen
              </button>
              <button
                onClick={() => setActiveTab('running')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer ${
                  activeTab === 'running'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Laufende Investitionen
              </button>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:px-6 w-full overflow-x-hidden">
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'planned' && renderPlannedInvestments()}
            {activeTab === 'running' && renderRunningInvestments()}
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Planned Investment Modal */}
      <Modal
        isOpen={isPlannedModalOpen}
        onClose={() => setIsPlannedModalOpen(false)}
        title="Neue geplante Investition"
        maxWidth="lg"
      >
        <form onSubmit={handlePlannedSubmit} className="space-y-4">
          <Input
            label="Name"
            value={plannedForm.name}
            onChange={(e) => setPlannedForm(prev => ({ ...prev, name: e.target.value }))}
            required
          />
          
          <Input
            label="Beschreibung"
            value={plannedForm.description}
            onChange={(e) => setPlannedForm(prev => ({ ...prev, description: e.target.value }))}
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Geplanter Betrag (CHF)"
              type="number"
              step="0.01"
              value={plannedForm.plannedAmount}
              onChange={(e) => setPlannedForm(prev => ({ ...prev, plannedAmount: e.target.value }))}
              required
            />
            
            <Input
              label="Geplantes Datum"
              type="date"
              value={plannedForm.plannedDate}
              onChange={(e) => setPlannedForm(prev => ({ ...prev, plannedDate: e.target.value }))}
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={plannedForm.category}
              onChange={(value) => setPlannedForm(prev => ({ ...prev, category: value as string }))}
              required
            />
            
            <Select
              label="Priorität"
              options={[
                { value: 'hoch', label: 'Hoch' },
                { value: 'mittel', label: 'Mittel' },
                { value: 'niedrig', label: 'Niedrig' }
              ]}
              value={plannedForm.priority}
              onChange={(value) => setPlannedForm(prev => ({ ...prev, priority: value as 'hoch' | 'mittel' | 'niedrig' }))}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={plannedForm.notes}
              onChange={(e) => setPlannedForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={() => setIsPlannedModalOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit">Speichern</Button>
          </div>
        </form>
      </Modal>

      {/* Running Investment Modal */}
      <Modal
        isOpen={isRunningModalOpen}
        onClose={() => setIsRunningModalOpen(false)}
        title="Neue laufende Investition"
        maxWidth="lg"
      >
        <form onSubmit={handleRunningSubmit} className="space-y-4">
          <Input
            label="Name"
            value={runningForm.name}
            onChange={(e) => handleRunningFormChange('name', e.target.value)}
            required
          />
          
          <Input
            label="Beschreibung"
            value={runningForm.description}
            onChange={(e) => handleRunningFormChange('description', e.target.value)}
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Gesamtbetrag (CHF)"
              type="number"
              step="0.01"
              value={runningForm.totalAmount}
              onChange={(e) => handleRunningFormChange('totalAmount', e.target.value)}
              placeholder="z.B. 10000"
              required
            />
            
            <Input
              label="Anzahl Monate"
              type="number"
              min="1"
              value={runningForm.months}
              onChange={(e) => handleRunningFormChange('months', e.target.value)}
              placeholder="z.B. 12"
              required
            />
          </div>

          {/* Automatic Monthly Payment Display */}
          {runningForm.monthlyPayment && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-800 font-medium">Automatisch berechnete monatliche Rate:</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {runningForm.totalAmount && runningForm.months && 
                      `CHF ${runningForm.totalAmount} ÷ ${runningForm.months} Monate`
                    }
                  </p>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  CHF {parseFloat(runningForm.monthlyPayment).toFixed(2)}
                </p>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Startdatum"
              type="date"
              value={runningForm.startDate}
              onChange={(e) => handleRunningFormChange('startDate', e.target.value)}
              required
            />
            
            <Input
              label="Enddatum (automatisch berechnet)"
              type="date"
              value={runningForm.expectedEndDate}
              onChange={(e) => handleRunningFormChange('expectedEndDate', e.target.value)}
              disabled
              className="bg-gray-100"
            />
          </div>
          
          <Input
            label="Kreditgeber / Investor"
            value={runningForm.lender}
            onChange={(e) => handleRunningFormChange('lender', e.target.value)}
            placeholder="z.B. Bank, Privatperson, etc."
            required
          />
          
          <Input
            label="Zinssatz (%)"
            type="number"
            step="0.01"
            value={runningForm.interestRate}
            onChange={(e) => handleRunningFormChange('interestRate', e.target.value)}
            placeholder="z.B. 3.5"
          />

          {/* Kostenpositionen Section */}
          <div className="border-t-2 border-gray-200 pt-4 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                <i className="ri-file-list-3-line mr-2"></i>
                Kostenpositionen
              </h3>
              <span className="text-sm text-gray-600">
                {runningForm.positions.length} Position{runningForm.positions.length !== 1 ? 'en' : ''}
              </span>
            </div>

            {/* Existing Positions List */}
            {runningForm.positions.length > 0 && (
              <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                {runningForm.positions.map((position) => (
                  <div key={position.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-gray-900">{position.name}</h4>
                          <span className="font-bold text-gray-900">{formatCurrency(position.amount)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{position.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>
                            <i className="ri-calendar-line mr-1"></i>
                            Fällig: {new Date(position.dueDate).toLocaleDateString('de-DE')}
                          </span>
                          {position.notes && (
                            <span className="truncate max-w-xs" title={position.notes}>
                              <i className="ri-sticky-note-line mr-1"></i>
                              {position.notes}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePositionFromForm(position.id)}
                        className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded cursor-pointer"
                        title="Position entfernen"
                      >
                        <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Position Form */}
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">
                <i className="ri-add-circle-line mr-2"></i>
                Neue Position hinzufügen
              </h4>
              
              <div className="space-y-3">
                <Input
                  label="Positionsname"
                  value={positionForm.name}
                  onChange={(e) => setPositionForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Automat, Lieferung, Fahrtkosten"
                />
                
                <Input
                  label="Beschreibung"
                  value={positionForm.description}
                  onChange={(e) => setPositionForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detaillierte Beschreibung"
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    label="Betrag (CHF)"
                    type="number"
                    step="0.01"
                    value={positionForm.amount}
                    onChange={(e) => setPositionForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                  />
                  
                  <Input
                    label="Fälligkeitsdatum"
                    type="date"
                    value={positionForm.dueDate}
                    onChange={(e) => setPositionForm(prev => ({ ...prev, dueDate: e.target.value }))}
                  />
                </div>
                
                <Input
                  label="Notizen (optional)"
                  value={positionForm.notes}
                  onChange={(e) => setPositionForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Zusätzliche Informationen"
                />
                
                <button
                  type="button"
                  onClick={handleAddPositionToForm}
                  disabled={!positionForm.name || !positionForm.description || !positionForm.amount}
                  className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Position zur Liste hinzufügen
                </button>
              </div>
            </div>

            {runningForm.positions.length > 0 && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-800 font-medium">Gesamtsumme aller Positionen:</span>
                  <span className="text-lg font-bold text-blue-600">
                    {formatCurrency(runningForm.positions.reduce((sum, pos) => sum + pos.amount, 0))}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <div className="border-t-2 border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={runningForm.notes}
              onChange={(e) => handleRunningFormChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={3}
            />
          </div>
          
          <div className="flex justify-end space-x-3 pt-4 border-t-2 border-gray-200">
            <Button type="button" variant="secondary" onClick={() => {
              setIsRunningModalOpen(false);
              setRunningForm({
                name: '',
                description: '',
                totalAmount: '',
                months: '',
                startDate: '',
                expectedEndDate: '',
                lender: '',
                interestRate: '',
                monthlyPayment: '',
                notes: '',
                positions: []
              });
              setPositionForm({
                name: '',
                description: '',
                amount: '',
                dueDate: '',
                notes: ''
              });
            }}>
              Abbrechen
            </Button>
            <Button type="submit">Investition speichern</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Running Investment Modal */}
      <Modal
        isOpen={isEditRunningModalOpen}
        onClose={() => {
          setIsEditRunningModalOpen(false);
          setEditingRunningInvestment(null);
          setRunningForm({
            name: '',
            description: '',
            totalAmount: '',
            months: '',
            startDate: '',
            expectedEndDate: '',
            lender: '',
            interestRate: '',
            monthlyPayment: '',
            notes: '',
            positions: []
          });
        }}
        title="Laufende Investition bearbeiten"
        maxWidth="lg"
      >
        <form onSubmit={handleUpdateRunningInvestment} className="space-y-4">
          <Input
            label="Name"
            value={runningForm.name}
            onChange={(e) => handleRunningFormChange('name', e.target.value)}
            required
          />
          
          <Input
            label="Beschreibung"
            value={runningForm.description}
            onChange={(e) => handleRunningFormChange('description', e.target.value)}
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Gesamtbetrag (CHF)"
              type="number"
              step="0.01"
              value={runningForm.totalAmount}
              onChange={(e) => handleRunningFormChange('totalAmount', e.target.value)}
              placeholder="z.B. 10000"
              required
            />
            
            <Input
              label="Anzahl Monate"
              type="number"
              min="1"
              value={runningForm.months}
              onChange={(e) => handleRunningFormChange('months', e.target.value)}
              placeholder="z.B. 12"
              required
            />
          </div>

          {/* Automatic Monthly Payment Display */}
          {runningForm.monthlyPayment && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-800 font-medium">Automatisch berechnete monatliche Rate:</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {runningForm.totalAmount && runningForm.months && 
                      `CHF ${runningForm.totalAmount} ÷ ${runningForm.months} Monate`
                    }
                  </p>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  CHF {parseFloat(runningForm.monthlyPayment).toFixed(2)}
                </p>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Startdatum"
              type="date"
              value={runningForm.startDate}
              onChange={(e) => handleRunningFormChange('startDate', e.target.value)}
              required
            />
            
            <Input
              label="Enddatum (automatisch berechnet)"
              type="date"
              value={runningForm.expectedEndDate}
              onChange={(e) => handleRunningFormChange('expectedEndDate', e.target.value)}
              disabled
              className="bg-gray-100"
            />
          </div>
          
          <Input
            label="Kreditgeber / Investor"
            value={runningForm.lender}
            onChange={(e) => handleRunningFormChange('lender', e.target.value)}
            placeholder="z.B. Bank, Privatperson, etc."
            required
          />
          
          <Input
            label="Zinssatz (%)"
            type="number"
            step="0.01"
            value={runningForm.interestRate}
            onChange={(e) => handleRunningFormChange('interestRate', e.target.value)}
            placeholder="z.B. 3.5"
          />

          <div className="border-t-2 border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={runningForm.notes}
              onChange={(e) => handleRunningFormChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={3}
            />
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <i className="ri-information-line mr-2"></i>
              <strong>Hinweis:</strong> Wenn Sie den Gesamtbetrag oder die Laufzeit ändern, wird der Rückzahlungsplan automatisch neu berechnet. Bereits bezahlte Monate bleiben als bezahlt markiert.
            </p>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4 border-t-2 border-gray-200">
            <Button type="button" variant="secondary" onClick={() => {
              setIsEditRunningModalOpen(false);
              setEditingRunningInvestment(null);
              setRunningForm({
                name: '',
                description: '',
                totalAmount: '',
                months: '',
                startDate: '',
                expectedEndDate: '',
                lender: '',
                interestRate: '',
                monthlyPayment: '',
                notes: '',
                positions: []
              });
            }}>
              Abbrechen
            </Button>
            <Button type="submit">Änderungen speichern</Button>
          </div>
        </form>
      </Modal>

      {/* Invoice Modal */}
      <Modal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        title="Neue Rechnung"
        maxWidth="lg"
      >
        <form onSubmit={handleInvoiceSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Rechnungsnummer"
              value={invoiceForm.invoiceNumber}
              onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoiceNumber: e.target.value }))}
              required
            />
            
            <Input
              label="Lieferant"
              value={invoiceForm.supplier}
              onChange={(e) => setInvoiceForm(prev => ({ ...prev, supplier: e.target.value }))}
              required
            />
          </div>
          
          <Input
            label="Beschreibung"
            value={invoiceForm.description}
            onChange={(e) => setInvoiceForm(prev => ({ ...prev, description: e.target.value }))}
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Betrag (CHF)"
              type="number"
              step="0.01"
              value={invoiceForm.amount}
              onChange={(e) => setInvoiceForm(prev => ({ ...prev, amount: e.target.value }))}
              required
            />
            
            <Input
              label="Fälligkeitsdatum"
              type="date"
              value={invoiceForm.dueDate}
              onChange={(e) => setInvoiceForm(prev => ({ ...prev, dueDate: e.target.value }))}
              required
            />
          </div>
          
          <Select
            label="Kategorie"
            options={invoiceCategories.map(cat => ({ value: cat, label: cat }))}
            value={invoiceForm.category}
            onChange={(value) => setInvoiceForm(prev => ({ ...prev, category: value as string }))}
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={invoiceForm.notes}
              onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={3}
              placeholder="Zusätzliche Informationen zur Rechnung..."
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={() => setIsInvoiceModalOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit">Speichern</Button>
          </div>
        </form>
      </Modal>

      {/* Sale Modal */}
      <Modal
        isOpen={isSaleModalOpen}
        onClose={() => setIsSaleModalOpen(false)}
        title="Neuer Verkauf"
        maxWidth="lg"
      >
        <form onSubmit={handleSaleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Datum"
              type="date"
              value={saleForm.date}
              onChange={(e) => setSaleForm(prev => ({ ...prev, date: e.target.value }))}
              required
            />
            
            <Select
              label="Verkaufstyp"
              options={[
                { value: 'automat_verkauf', label: 'Automat Verkauf' },
                { value: 'produkt_verkauf', label: 'Produkt Verkauf' },
                { value: 'sonstiges', label: 'Sonstiges' }
              ]}
              value={saleForm.type}
              onChange={(value) => setSaleForm(prev => ({ ...prev, type: value as 'automat_verkauf' | 'produkt_verkauf' | 'sonstiges' }))}
              required
            />
          </div>
          
          <Input
            label="Beschreibung"
            value={saleForm.description}
            onChange={(e) => setSaleForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="z.B. Verkauf von Automat XY-123"
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Verkaufsbetrag (CHF)"
              type="number"
              step="0.01"
              value={saleForm.amount}
              onChange={(e) => setSaleForm(prev => ({ ...prev, amount: e.target.value }))}
              required
            />
            
            <Input
              label="Käufer"
              value={saleForm.buyer}
              onChange={(e) => setSaleForm(prev => ({ ...prev, buyer: e.target.value }))}
              placeholder="Name des Käufers"
              required
            />
          </div>
          
          <Input
            label="Zahlungsmethode"
            value={saleForm.paymentMethod}
            onChange={(e) => setSaleForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
            placeholder="z.B. Banküberweisung, Bar, etc."
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={saleForm.notes}
              onChange={(e) => setSaleForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              rows={3}
              placeholder="Zusätzliche Informationen zum Verkauf..."
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={() => setIsSaleModalOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit">Speichern</Button>
          </div>
        </form>
      </Modal>

      {/* Investment Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedInvestment(null);
          setRepaymentSchedule([]);
        }}
        title={selectedInvestment?.name || 'Investitionsdetails'}
        maxWidth="4xl"
      >
        {selectedInvestment && (
          <div className="space-y-6">
            {/* Investment Summary */}
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-lg border-2 border-orange-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Gesamtbetrag (Investition)</p>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(selectedInvestment.totalAmount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Kostenpositionen Gesamt</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(
                      selectedInvestment.positions?.reduce((sum, pos) => sum + pos.amount, 0) || 0
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Bezahlt (Positionen)</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(
                      selectedInvestment.positions
                        ?.filter(pos => pos.isPaid)
                        .reduce((sum, pos) => sum + pos.amount, 0) || 0
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Verbleibend (Positionen)</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(
                      selectedInvestment.positions
                        ?.filter(pos => !pos.isPaid)
                        .reduce((sum, pos) => sum + pos.amount, 0) || 0
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Positions */}
              <div>
                {/* Positions Header */}
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    <i className="ri-file-list-3-line mr-2"></i>
                    Kostenpositionen
                  </h3>
                  <button
                    onClick={() => setIsPositionModalOpen(true)}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Position hinzufügen
                  </button>
                </div>

                {/* Positions List */}
                {!selectedInvestment.positions || selectedInvestment.positions.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <i className="ri-file-list-3-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">Noch keine Positionen vorhanden</p>
                    <p className="text-sm text-gray-400 mt-2">Fügen Sie Positionen wie Automat, Lieferung, Fahrtkosten, etc. hinzu</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {/* Sort positions: first with dueDate (chronologically), then without dueDate */}
                    {[...selectedInvestment.positions]
                      .sort((a, b) => {
                        // If both have dueDate, sort chronologically
                        if (a.dueDate && b.dueDate) {
                          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
                        }
                        // If only a has dueDate, a comes first
                        if (a.dueDate && !b.dueDate) return -1;
                        // If only b has dueDate, b comes first
                        if (!a.dueDate && b.dueDate) return 1;
                        // If neither has dueDate, maintain original order
                        return 0;
                      })
                      .map(position => {
                      const isOverdue = position.dueDate && !position.isPaid && new Date(position.dueDate) < new Date();
                      return (
                        <div 
                          key={position.id} 
                          className={`border-2 rounded-lg p-4 ${
                            position.isPaid ? 'bg-green-50 border-green-200' : 
                            isOverdue ? 'bg-red-50 border-red-200' : 
                            'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-4 flex-1">
                              {/* Checkbox */}
                              <button
                                onClick={() => togglePositionPaid(position.id)}
                                className="mt-1 cursor-pointer"
                              >
                                {position.isPaid ? (
                                  <i className="ri-checkbox-circle-fill text-3xl text-green-600"></i>
                                ) : (
                                  <i className="ri-checkbox-blank-circle-line text-3xl text-gray-400 hover:text-gray-600"></i>
                                )}
                              </button>

                              {/* Position Info */}
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <h4 className="font-bold text-gray-900">{position.name}</h4>
                                    <p className="text-sm text-gray-600">{position.description}</p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <p className="text-lg font-bold text-gray-900">{formatCurrency(position.amount)}</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  {position.dueDate ? (
                                    <div>
                                      <span className="text-gray-600">Fällig am: </span>
                                      <span className={`font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                                        {new Date(position.dueDate).toLocaleDateString('de-DE')}
                                        {isOverdue && ' ⚠️ Überfällig'}
                                      </span>
                                    </div>
                                  ) : (
                                    <div>
                                      <span className="text-gray-600">Fälligkeitsdatum: </span>
                                      <span className="font-medium text-gray-500">Nicht festgelegt</span>
                                    </div>
                                  )}
                                  {position.isPaid && position.paidDate && (
                                    <div>
                                      <span className="text-gray-600">Bezahlt am: </span>
                                      <span className="font-medium text-green-600">
                                        {new Date(position.paidDate).toLocaleDateString('de-DE')}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {position.notes && (
                                  <p className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                                    {position.notes}
                                  </p>
                                )}

                                {/* Invoice Upload/Display */}
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  {position.invoiceUrl ? (
                                    <div className="flex items-center justify-between bg-blue-50 p-2 rounded">
                                      <div className="flex items-center">
                                        <i className="ri-file-text-line text-blue-600 mr-2"></i>
                                        <span className="text-sm text-blue-900">Rechnung vorhanden</span>
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openBase64File(position.invoiceUrl, `Rechnung-${position.name}`);
                                        }}
                                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 cursor-pointer whitespace-nowrap"
                                      >
                                        <i className="ri-eye-line mr-1"></i>
                                        Anzeigen
                                      </button>
                                    </div>
                                  ) : (
                                    <div>
                                      <label className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded cursor-pointer hover:bg-gray-200 transition-colors">
                                        <i className="ri-upload-line mr-2"></i>
                                        <span className="text-sm">Rechnung hochladen</span>
                                        <input
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          className="hidden"
                                          onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                              handleInvoiceUpload(position.id, file);
                                            }
                                          }}
                                        />
                                      </label>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditPosition(position)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer"
                                title="Position bearbeiten"
                              >
                                <i className="ri-edit-line text-lg w-5 h-5 flex items-center justify-center"></i>
                              </button>
                              <button
                                onClick={() => deletePosition(position.id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors cursor-pointer"
                                title="Position löschen"
                              >
                                <i className="ri-delete-bin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right Column - Repayment Plan */}
              <div>
                <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    <i className="ri-calendar-check-line mr-2"></i>
                    Rückzahlungsplan
                  </h4>
                  {repaymentSchedule.length > 0 ? (
                    <>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-white rounded-lg p-3">
                          <span className="text-xs text-gray-600">Monatliche Rate</span>
                          <p className="font-bold text-lg text-gray-900">
                            {formatCurrency(repaymentSchedule[0]?.amount || 0)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Basiert auf Gesamtbetrag
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <span className="text-xs text-gray-600">Zurückbezahlt</span>
                          <p className="font-bold text-lg text-green-600">
                            {formatCurrency(repaymentSchedule.filter(s => s.isPaid).reduce((sum, s) => sum + s.amount, 0))}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <span className="text-xs text-gray-600">Noch offen</span>
                          <p className="font-bold text-lg text-orange-600">
                            {formatCurrency(repaymentSchedule.filter(s => !s.isPaid).reduce((sum, s) => sum + s.amount, 0))}
                          </p>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-600">Fortschritt</span>
                          <span className="font-semibold">
                            {repaymentSchedule.filter(s => s.isPaid).length} / {repaymentSchedule.length} Monate
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-teal-600 to-blue-600 h-3 rounded-full transition-all"
                            style={{
                              width: `${(repaymentSchedule.filter(s => s.isPaid).length / repaymentSchedule.length) * 100}%`
                            }}
                          ></div>
                        </div>
                      </div>

                      {repaymentSchedule.filter(s => !s.isPaid).length > 0 && (
                        <div className="bg-white rounded-lg p-3 mb-4">
                          <span className="text-xs text-gray-600">Restlaufzeit</span>
                          <p className="font-bold text-lg text-gray-900">
                            {repaymentSchedule.filter(s => !s.isPaid).length} Monate
                          </p>
                        </div>
                      )}

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-800">
                          <i className="ri-information-line mr-1"></i>
                          Die monatlichen Zahlungen basieren auf dem Gesamtbetrag von {formatCurrency(selectedInvestment?.totalAmount || 0)} verteilt über {repaymentSchedule.length} Monate.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <i className="ri-information-line mr-2"></i>
                        Der Rückzahlungsplan wird automatisch generiert, wenn Start- und Enddatum vorhanden sind.
                      </p>
                    </div>
                  )}
                </div>

                {/* Monthly Repayment Schedule */}
                {repaymentSchedule.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Monatliche Zahlungen</h4>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {repaymentSchedule.map((item) => {
                        const isOverdue = !item.isPaid && new Date(item.dueDate) < new Date();
                        const isEditing = editingRepayment?.month === item.month;
                        
                        return (
                          <div
                            key={item.month}
                            className={`border rounded-lg p-3 ${
                              item.isPaid
                                ? 'bg-green-50 border-green-200'
                                : isOverdue
                                ? 'bg-red-50 border-red-200'
                                : 'bg-white border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{item.monthName}</span>
                                  {item.isPaid && (
                                    <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full whitespace-nowrap">
                                      Bezahlt
                                    </span>
                                  )}
                                  {isOverdue && (
                                    <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded-full whitespace-nowrap">
                                      Überfällig
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">
                                  Fällig: {new Date(item.dueDate).toLocaleDateString('de-DE')}
                                </p>
                                {item.isPaid && item.paidDate && (
                                  <p className="text-sm text-green-600">
                                    Bezahlt am: {new Date(item.paidDate).toLocaleDateString('de-DE')}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {isEditing ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={repaymentEditForm.amount}
                                      onChange={(e) => setRepaymentEditForm({ amount: e.target.value })}
                                      className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                                      placeholder="Betrag"
                                    />
                                    <button
                                      onClick={handleUpdateRepayment}
                                      className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer"
                                      title="Speichern"
                                    >
                                      <i className="ri-check-line text-lg w-4 h-4 flex items-center justify-center"></i>
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingRepayment(null);
                                        setRepaymentEditForm({ amount: '' });
                                      }}
                                      className="p-1.5 bg-gray-400 text-white rounded hover:bg-gray-500 cursor-pointer"
                                      title="Abbrechen"
                                    >
                                      <i className="ri-close-line text-lg w-4 h-4 flex items-center justify-center"></i>
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="font-bold text-gray-900">
                                      {formatCurrency(item.amount)}
                                    </span>
                                    <button
                                      onClick={() => handleEditRepayment(item)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-100 rounded cursor-pointer"
                                      title="Betrag bearbeiten"
                                    >
                                      <i className="ri-edit-line text-lg w-4 h-4 flex items-center justify-center"></i>
                                    </button>
                                    <button
                                      onClick={() => toggleRepaymentPaid(item)}
                                      className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${
                                        item.isPaid
                                          ? 'bg-green-600 text-white'
                                          : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                                      }`}
                                      title={item.isPaid ? 'Als unbezahlt markieren' : 'Als bezahlt markieren'}
                                    >
                                      <i className="ri-check-line text-xl"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Confirmation Upload */}
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              {item.confirmationFile ? (
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      openBase64File(item.confirmationFile, `Zahlungsbestätigung-${item.monthName}`);
                                    }}
                                    className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer"
                                  >
                                    <i className="ri-file-text-line"></i>
                                    Bestätigung anzeigen
                                  </button>
                                  <label className="cursor-pointer text-xs text-gray-600 hover:text-gray-900">
                                    <i className="ri-upload-line mr-1"></i>
                                    Ersetzen
                                    <input
                                      type="file"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleRepaymentConfirmationUpload(item, file);
                                      }}
                                      className="hidden"
                                    />
                                  </label>
                                </div>
                              ) : (
                                <label className="cursor-pointer text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1">
                                  <i className="ri-upload-line"></i>
                                  Zahlungsbestätigung hochladen
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleRepaymentConfirmationUpload(item, file);
                                    }}
                                    className="hidden"
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Position Modal */}
      <Modal
        isOpen={isPositionModalOpen}
        onClose={() => {
          setIsPositionModalOpen(false);
          setEditingPosition(null);
          setPositionForm({
            name: '',
            description: '',
            amount: '',
            dueDate: '',
            notes: ''
          });
        }}
        title={editingPosition ? 'Position bearbeiten' : 'Neue Position hinzufügen'}
        maxWidth="lg"
      >
        <form onSubmit={editingPosition ? handleUpdatePosition : handlePositionSubmit} className="space-y-4">
          <Input
            label="Positionsname"
            value={positionForm.name}
            onChange={(e) => setPositionForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="z.B. Automat, Lieferung, Fahrtkosten"
            required
          />
          
          <Input
            label="Beschreibung"
            value={positionForm.description}
            onChange={(e) => setPositionForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Detaillierte Beschreibung der Position"
            required
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Betrag (CHF)"
              type="number"
              step="0.01"
              value={positionForm.amount}
              onChange={(e) => setPositionForm(prev => ({ ...prev, amount: e.target.value }))}
              required
            />
            
            <Input
              label="Fälligkeitsdatum (optional)"
              type="date"
              value={positionForm.dueDate}
              onChange={(e) => setPositionForm(prev => ({ ...prev, dueDate: e.target.value }))}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={positionForm.notes}
              onChange={(e) => setPositionForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={3}
              placeholder="Zusätzliche Informationen..."
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsPositionModalOpen(false);
                setEditingPosition(null);
                setPositionForm({
                  name: '',
                  description: '',
                  amount: '',
                  dueDate: '',
                  notes: ''
                });
              }}
            >
              Abbrechen
            </Button>
            <Button type="submit">
              {editingPosition ? 'Änderungen speichern' : 'Position hinzufügen'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
